import { TYPES } from '../../render/constants';
import { Rect } from '../region';

export function getCurrentSelectableNodes(stageContext, ignoreIds = []) {
  const allNodes = stageContext.api.getNodes();
  const { action, scale, interactive } = stageContext.providers;

  const scaleValue = scale.scale;
  const viewport = Rect.from({
    x: -scale.translateX / scaleValue,
    y: -scale.translateY / scaleValue,
    w: stageContext.context.canvas.width / scaleValue,
    h: stageContext.context.canvas.height / scaleValue,
  });

  if (action.isMetaKeyPressed || interactive.selectMode === 'deep') {
    const nodes = allNodes
      .filter((n) => !n.hidden)
      .filter((node) => {
        const rect = stageContext.api.getNodeRegion(node).rect;
        return viewport.intersection(rect);
      });
    return sortByRenderIndex(stageContext, nodes);
  }
  // root nodes
  const allFrameNodes = stageContext.api.getFrames();
  const rootNodes = stageContext.api.getChildren(null).filter((n) => n.type !== TYPES.frame);
  const hasParentNodes = allNodes.filter((node) => node.parentId);

  const frameNodes = allFrameNodes.filter((frame) => viewport.intersection(frame));

  // selected nodes
  const selectedNodes = [...stageContext.api.getSelectedNodes()];

  // selected nodes and their siblings and ancestors
  const selectedNodesRelated = [...selectedNodes, ...frameNodes];

  frameNodes.forEach((frame) => {
    const children = stageContext.api.getChildren(frame.id);
    add(children);
  });

  function add(nodes) {
    nodes.forEach((node) => {
      if (selectedNodesRelated.includes(node)) return;
      selectedNodesRelated.push(node);
    });
  }

  // siblings
  selectedNodes
    .map((n) => n.parentId)
    .filter(Boolean)
    .forEach((id) => {
      add(hasParentNodes.filter((n) => n.parentId === id));
    });
  // ancestors
  selectedNodes.forEach((node) => {
    const ancestors = stageContext.api.getAncenstors(node);
    add(ancestors);
  });

  // parentNodeNotInvisible
  const list = selectedNodesRelated
    .filter((node) => !node.hidden)
    .filter((node) => {
      const parent = stageContext.api.getNode(node.parentId);
      if (!parent) {
        return true;
      }
      if (parent.visible === false) return false;
      if (parent.hidden === true) return false;
      return true;
    });

  list.push(...rootNodes);

  return sortByRenderIndex(
    stageContext,
    list.filter((node) => {
      if (node.visible === false) return false;
      if (node.hidden === true) return false;
      if (node.isSeedeBadge === true) return false;
      // 允许被锁定的节点本身参与选择
      // 但排除任意拥有被锁定祖先的节点（锁定节点的子孙不能被选中）
      const ancestors = stageContext.api.getAncenstors(node.id);
      if (ancestors && ancestors.find((n) => n.locked === true)) return false;
      if (ignoreIds && ignoreIds.includes(node.id)) return false;
      return true;
    }),
  );
}

export function sortByRenderIndex(stageContext, nodes, frameNodes, type = 'render') {
  // Build a fast lookup set for target node ids
  const targetIdSet = new Set(nodes.map((n) => n.id));

  // Build children map once to avoid repeated getChildren() and sorting
  // parentId (null for roots) -> sorted children array
  const childrenByParent = new Map();
  const allNodes = stageContext.api.getNodes() || [];
  for (const n of allNodes) {
    const pid = n.parentId || null;
    if (!childrenByParent.has(pid)) childrenByParent.set(pid, []);
    childrenByParent.get(pid).push(n);
  }
  // Sort children per parent by index only once
  for (const [, list] of childrenByParent) {
    list.sort((a, b) => (a.index < b.index ? -1 : a.index > b.index ? 1 : 0));
  }

  // Determine traversal roots
  const roots = frameNodes && frameNodes.length ? frameNodes : childrenByParent.get(null) || [];

  // Assign render order in a single traversal
  let order = 0;
  const orderMap = new Map();
  const isRenderOrder = type === 'render';

  const pushIfTarget = (node) => {
    if (!node) return;
    if (!targetIdSet.has(node.id)) return;
    orderMap.set(node.id, order++);
  };

  const traverseRender = (node) => {
    // render: parent first, then children
    pushIfTarget(node);
    const children = childrenByParent.get(node.id) || [];
    for (const child of children) traverseRender(child);
  };

  const traverseLayer = (node) => {
    // layer: children first, then parent
    const children = childrenByParent.get(node.id) || [];
    for (const child of children) traverseLayer(child);
    pushIfTarget(node);
  };

  const traverse = isRenderOrder ? traverseRender : traverseLayer;
  for (const root of roots) traverse(root);

  // Sort the input nodes by computed order (desc to match previous behavior)
  const sorted = nodes
    .filter((n) => orderMap.has(n.id))
    .sort((a, b) => orderMap.get(b.id) - orderMap.get(a.id));
  return sorted;
}
